"use client";

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
// --- 1. IMPORT RoomDraftDto ---
import {
  RoomDraftDto,
  PropertyPhotoDto,
  RoomPhotoDto,
  resumePartnerTokenRefresh,
} from "@/lib/api";

// --- 2. UPDATE THE OnboardingData INTERFACE ---
export interface OnboardingData {
  coordinates: any;
  locationId: number | undefined;
  // Catalog fields
  parentCatalogId: number | null;
  parentCatalogCode: string | null;
  subCatalogId: number | null;

  // From hotel-name step
  hotelName: string;
  starRating: number;
  // Persisted fields for basic info step
  isPropertyManager?: boolean;
  chainName?: string;

  // From hotel-location step
  address: {
    country: string;
    latitude: number;
    longitude: number;
    address1: string;
    address2: string;
    province: string;
    city: string;
    zipCode: string;
    locationUid?: number;
  };

  // From hotel-details step
  hotelDetails: {
    description: string;
    checkinTime: string;
    checkoutTime: string;
    accessModes: string[];
    amenityCodes: string[];
    paymentMethods: string[];
    cancellationPolicy: string;
  };

  // From hotel-room step
  rooms: RoomDraftDto[]; // <-- Use the API type
  providesBreakfast: boolean;

  // From hotel-photos step
  propertyPhotos: PropertyPhotoDto[];
  roomPhotos: RoomPhotoDto[];

  // From hotel-payment step
  account: {
    email: string;
    // addressInfo: string;
    // payout: string;
    firstName: string;
    lastName: string;
    nationality: string;
    phone: string;
    preferredLanguage: string;
  };
  bankDetails: {
    accountHolderName: string;
    bankName: string;
    branchName: string;
    accountNumber: string;
    swiftCode?: string;
  };
  // Added for payment step persistence
  accountAddress: {
    zipCode: string;
    country: string | undefined;
    address1: string;
    address2: string;
    province: string;
    city: string;
  };
  payoutMethods: string[];
}

// --- 3. UPDATE THE INITIAL STATE ---
const initialOnboardingData: OnboardingData = {
  parentCatalogId: null,
  parentCatalogCode: null,
  subCatalogId: null,
  hotelName: "",
  starRating: 0,
  isPropertyManager: false,
  chainName: "",
  address: {
    address1: "",
    address2: "",
    province: "",
    city: "",
    zipCode: "",
    locationUid: undefined,
    country: "",
    latitude: 0,
    longitude: 0,
  },
  hotelDetails: {
    checkinTime: "",
    checkoutTime: "",
    accessModes: [],
    amenityCodes: [],
    paymentMethods: [],
    cancellationPolicy: "",
    description: "",
  },
  rooms: [],
  providesBreakfast: false,
  propertyPhotos: [],
  roomPhotos: [],
  account: {
    firstName: "",
    lastName: "",
    nationality: "",
    phone: "",
    preferredLanguage: "",
    email: "",
  },

  bankDetails: {
    accountHolderName: "",
    bankName: "",
    branchName: "",
    accountNumber: "",
    swiftCode: "",
  },
  accountAddress: {
    address1: "",
    address2: "",
    province: "",
    city: "",
    zipCode: "",
    country: undefined,
  },
  payoutMethods: [],
  coordinates: undefined,
  locationId: undefined,
};

// --- (Context Type and Creation are the same) ---
interface OnboardingContextType {
  partnerId: number | null;
  setPartnerId: (id: number | null) => void;
  sessionId: number | null;
  setSessionId: (id: number | null) => void;
  onboardingData: OnboardingData;
  updateData: (partialData: Partial<OnboardingData>) => void;
  resetData: () => void;
  isInitialized: boolean;
}

const HotelOnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined,
);

export const HotelOnboardingProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // --- 4. Use the new initial state ---
  const [onboardingData, setOnboardingData] = useState<OnboardingData>(
    initialOnboardingData,
  );

  useEffect(() => {
    try {
      const storedId = localStorage.getItem("partnerId");
      if (storedId) {
        setPartnerId(parseInt(storedId, 10));
      }

      const storedSessionId = sessionStorage.getItem("sessionId");
      if (storedSessionId) {
        setSessionId(parseInt(storedSessionId, 10));
      }

      // Restart the silent partner-token refresh timer after a full page
      // reload (it only lives in memory, so a reload always kills it).
      resumePartnerTokenRefresh();
    } catch (error) {
      console.error("Failed to read from storage", error);
    } finally {
      setIsInitialized(true);
    }
  }, []);

  const setPartnerIdAndStore = (id: number | null) => {
    setPartnerId(id);
    if (typeof window === "undefined") return;
    if (id) {
      localStorage.setItem("partnerId", id.toString());
    } else {
      localStorage.removeItem("partnerId");
    }
  };

  const setSessionIdAndStore = (id: number | null) => {
    setSessionId(id);
    if (typeof window === "undefined") return;
    if (id) {
      sessionStorage.setItem("sessionId", id.toString());
    } else {
      sessionStorage.removeItem("sessionId");
    }
  };

  const updateData = (partialData: Partial<OnboardingData>) => {
    setOnboardingData((prevData) => ({
      ...prevData,
      ...partialData,
    }));
  };

  const resetData = () => {
    setOnboardingData(initialOnboardingData);
  };

  const value = {
    partnerId,
    setPartnerId: setPartnerIdAndStore,
    sessionId,
    setSessionId: setSessionIdAndStore,
    onboardingData,
    updateData,
    resetData,
    isInitialized,
  };

  return (
    <HotelOnboardingContext.Provider value={value}>
      {children}
    </HotelOnboardingContext.Provider>
  );
};

export const useHotelOnboarding = () => {
  const context = useContext(HotelOnboardingContext);
  if (context === undefined) {
    throw new Error(
      "useHotelOnboarding must be used within a HotelOnboardingProvider",
    );
  }
  return context;
};
